import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Inbox, ChevronRight } from "lucide-react";
import ProofCard from "@/components/ProofCard";
import CareerDocCard from "@/components/CareerDocCard";
import PfmpPortfolioCard from "@/components/PfmpPortfolioCard";

export default function PendingPanel() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/admin/pending-proofs").then(({ data }) => setItems(data)).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (items === null) return null;

  return (
    <div className="rounded-3xl border border-orange-200 bg-orange-50/60 shadow-sm p-5 mb-6 mt-8" data-testid="pending-panel">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-9 w-9 rounded-2xl bg-orange-500 text-white flex items-center justify-center shrink-0"><Inbox className="h-5 w-5" /></div>
        <div>
          <p className="font-display text-lg font-semibold text-slate-900 leading-tight">À évaluer</p>
          <p className="text-sm text-slate-500 font-medium" data-testid="pending-count">
            {items.length === 0 ? "Tout est à jour 🎉" : `${items.length} élément${items.length > 1 ? "s" : ""} en attente d'évaluation`}
          </p>
        </div>
      </div>
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((p) => (
            <button key={p.id} onClick={() => setActive(p)} data-testid={`pending-item-${p.id}`} className="w-full flex items-center gap-3 bg-white border border-orange-100 rounded-2xl px-4 py-3 text-left hover:border-orange-300 hover:shadow-sm transition-colors">
              <div className="h-9 w-9 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center font-display font-semibold shrink-0 text-sm">
                {(p.student_name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 truncate">{p.student_name}</p>
                <p className="text-xs text-slate-400 font-semibold truncate">
                  {p.kind === "career" ? `Recherche de stage · ${p.type_label}` : p.kind === "pfmp_portfolio" ? `Portfolio PFMP · ${p.pfmp_title}` : `${p.bloc} · ${p.competency_code} · ${p.seance_title}`}
                </p>
              </div>
              <span className="hidden sm:inline text-[11px] font-bold text-slate-400 whitespace-nowrap">{(p.created_at || "").slice(0, 10)}</span>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-orange-600 whitespace-nowrap">Évaluer <ChevronRight className="h-4 w-4" /></span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Évaluer — {active?.student_name}</DialogTitle>
            <DialogDescription>
              {active?.kind === "career" ? `Recherche de stage · ${active?.type_label}` : active?.kind === "pfmp_portfolio" ? `Portfolio PFMP · ${active?.pfmp_title}` : `${active?.bloc || ""} · ${active?.seance_title || ""}`}
            </DialogDescription>
          </DialogHeader>
          {active && active.kind === "career" && (
            <div className="mt-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1">Recherche de stage · {active.type_label}</p>
              <CareerDocCard doc={active} reviewMode onChanged={() => { setActive(null); load(); }} />
            </div>
          )}
          {active && active.kind === "pfmp_portfolio" && (
            <div className="mt-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1">Portfolio PFMP · {active.pfmp_title}</p>
              <PfmpPortfolioCard portfolio={active} reviewMode onChanged={() => { setActive(null); load(); }} />
            </div>
          )}
          {active && active.kind !== "career" && active.kind !== "pfmp_portfolio" && (
            <div className="mt-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1">{active.bloc} · {active.seance_title}</p>
              <ProofCard proof={active} reviewMode onChanged={() => { setActive(null); load(); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
